/**
 * @module app/router
 * @description Minimal hash router: #/ (browse), #/checkout, #/payment, #/thank-you.
 *
 * Hash routing works on any static host (Pages, a WordPress embed) with no
 * server rewrites, and the browser Back button works between steps.
 */
import { useEffect, useState } from "react";

/** Every screen in the giving journey. */
export type Route = "browse" | "checkout" | "payment" | "thank-you";

const ROUTES: readonly Route[] = ["browse", "checkout", "payment", "thank-you"];

/** @returns the Route for the current hash (unknown → browse). */
function readRoute(): Route {
    const slug: string = window.location.hash.replace(/^#\/?/, "");
    return (ROUTES as readonly string[]).includes(slug) ? (slug as Route) : "browse";
}

/** Navigates to a route and scrolls to the top of the new screen. */
export function navigate(route: Route): void {
    window.location.hash = route === "browse" ? "/" : `/${route}`;
    window.scrollTo({ top: 0 });
}

/** @returns the current route, re-rendering on hash changes. */
export function useRoute(): Route {
    const [route, setRoute] = useState<Route>(readRoute);
    useEffect(() => {
        const onChange = (): void => setRoute(readRoute());
        window.addEventListener("hashchange", onChange);
        return () => window.removeEventListener("hashchange", onChange);
    }, []);
    return route;
}
